import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import U2000DefaultForm from "../../../u2000Core/components/formControls/U2000DefaultForm";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../../u2000Core/hooks/handler/U2000UseGetHandlers";
import type { U2000FormSBusinessHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../../u2000Core/reducer/formReducer/U2000LUState";
import { U2000GridStateEnumPaginationDirection } from "../../../u2000Core/reducer/formReducer/U2000GridState";
import { useU2000SaveHandlers } from "../../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import { useU2000DeleteHandlers } from "../../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { useU2000SearchHandlers } from "../../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../../u2000Core/components/tables/basic/U2000BasicTable";
import { NS } from "../../U3129";
import { useU3129Context } from "../../U3129Context";
import {
  buildTimesheetUpdatePayload,
  doCreate,
  doDelete,
  doGet,
  doSearch,
  doUpdate,
  emptyTimesheet,
  ensureBankHoursList,
  luHandleApiError,
  normalizeTimesheetFromApi,
  type U3129EmployeeRef,
  type U3129FindEmployeeItem,
  type U3129Timesheet,
  type U3129SearchPayload,
  type U3129TimesheetSavePayload,
} from "../../U3129Service";
import {
  u2000ComputeTimesheetDerivedTotals,
  u2000MergeTimesheetDerivedTotalsInto,
} from "../../../u2000Core/tools/U2000TimesheetFormDerivedTotals";
import { u2000FormatTimesheetPeriodForBreadcrumb } from "../../../u2000Core/tools/U2000TimesheetPeriodBreadcrumbFormat";
import U3129TimesheetFormHeaderTab from "./U3129TimesheetFormHeaderTab";
import U3129HonorariumTab from "./U3129HonorariumTab";
import U3129AbsencesBanquesTab from "./U3129AbsencesBanquesTab";
import U3129ExpenseTab from "./U3129ExpenseTab";
import U3129EquipmentTab from "./U3129EquipmentTab";
import U3129TimesheetFormToolBar from "./U3129TimesheetFormToolBar";
import U3129TimesheetDeleteDlg from "../common/U3129TimesheetDeleteDlg";
import U2000Tab from "../../../u2000Core/components/tab/U2000Tab";
import { useU2000MinMdViewport } from "../../../u2000Core/hooks/useU2000MinMdViewport";
import U3129AddTimesheetDialog, { formatPeriodDateRange } from "../U3129AddTimesheetDialog";
import { useU2000LogicalUnitContext } from "../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { useU3129SubmitForApprovalHandlers } from "../../U3129UseSubmitForApprovalHandlers";
import { useU3129FindTasksForProjectHandlers } from "../../U3129UseFindProjectSubprojectHandlers";
import { u3129ResolveHonorariumCoherenceWarnings } from "../../tools/u3129ResolveHonorariumCoherenceWarnings";
import {
  type U3129HonorariumCoherenceWarningsState,
  u3129PreserveHonorariumClientRowIds,
} from "../../tools/u3129HonorariumCoherenceWarnings";
import { useU3129ReopenHandlers } from "../../U3129UseReopenHandlers";
import { useU3129InitForCreateHandlers } from "../../U3129UseInitForCreateHandlers";
import { u3129CollectTimesheetFormErrors } from "../../tools/U3129TimesheetFormValidation";
import {
  U3129_HIDDEN_EXPENSE_EQUIPMENT_TAB_IDS,
  u3129HideExpenseAndEquipmentTabs,
} from "../../tools/u3129HideExpenseAndEquipmentTabs";
import { equipmentTabHasValidationErrors } from "../../tools/u3129ValidateEquipmentLines";
import { expensesTabHasValidationErrors } from "../../tools/u3129ValidateExpenseLines";
import { honorariaTabHasValidationErrors } from "../../tools/u3129ValidateHonorariaLines";
import { bankHoursTabHasValidationErrors } from "../../tools/u3129ValidateBankHoursLines";
import { U2000_TIMESHEET_WEEK_DAYS } from "../../../u2000Core/tools/U2000TimesheetHonorariumDayTotals";
import { parseU3129ApprovalCorrectionLocationState } from "../../tools/u3129ApprovalCorrectionLocationState";
import { U3129TimesheetWeekViewProvider } from "./U3129TimesheetWeekViewContext";


function toFiniteNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/** Fil d’Ariane détail : segment employé (après le titre LU) puis période + statut (période sans jour de la semaine). */
function buildU3129TimesheetDetailBreadcrumbTrail(
  clean: U3129Timesheet,
  normalized: Pick<U3129Timesheet, "id" | "periodDate">,
  locale: string,
): { employeeLabel?: string; detailLabel: string } {
  const statusPart = clean.statusDisplay ? ` (${clean.statusDisplay})` : "";
  const periodIso = (normalized.periodDate ?? "").trim();
  const periodText =
    periodIso.length >= 10
      ? u2000FormatTimesheetPeriodForBreadcrumb(periodIso, locale)
      : (clean.periodDisplay || periodIso).trim();
  const detailLabel = periodText ? `${periodText}${statusPart}` : `${normalized.id ?? ""}${statusPart}`;
  const employeeLabel = (clean.employeeDisplay ?? "").trim() || undefined;
  return { employeeLabel, detailLabel };
}

const U3129TimesheetForm: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const {
    searchStateHandlers,
    gridStateHandlers,
    luStateHandlers,
    employee,
    employeeId,
    setEmployeeId,
    showAllEmployees,
    honorariumTasksByClientRowId,
    formActiveTab,
    setFormActiveTab,
    initLU,
  } = useU3129Context();

  const admIncludeSubProjectEnabled = initLU.personalization?.admIncludeSubProject === "true";
  const hideExpenseAndEquipmentTabs = u3129HideExpenseAndEquipmentTabs(initLU.personalization);
  const minMdViewport = useU2000MinMdViewport();
  const timesheetFormTabListOrientation = minMdViewport ? "vertical" : "horizontal";

  const [formState, formStateDispatch] = useReducer(u2000FormState, u2000IntFormState);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [closeTimesheetLoading, setCloseTimesheetLoading] = useState(false);
  const [reopenTimesheetLoading, setReopenTimesheetLoading] = useState(false);
  const [approvalCorrectionBusy, setApprovalCorrectionBusy] = useState(false);
  const [formTabListCollapsed, setFormTabListCollapsed] = useState(() => {
    try {
      return localStorage.getItem("u3129-form-tab-list-collapsed") === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("u3129-form-tab-list-collapsed", String(formTabListCollapsed));
    } catch {
      /* localStorage indisponible */
    }
  }, [formTabListCollapsed]);
  const [createInitLoading, setCreateInitLoading] = useState(false);
  const approvalCorrectionNav = useMemo(
    () => parseU3129ApprovalCorrectionLocationState(location.state),
    [location.state],
  );
  const isApprovalCorrection = approvalCorrectionNav != null;
  const { submitForApproval: submitForApprovalRequest } = useU3129SubmitForApprovalHandlers();
  const { fetchTasksResult } = useU3129FindTasksForProjectHandlers();
  const { reopen: reopenRequest } = useU3129ReopenHandlers();
  const [honorariumCoherenceWarnings, setHonorariumCoherenceWarnings] =
    useState<U3129HonorariumCoherenceWarningsState | null>(null);

  const honorariumCoherenceMessages = useMemo(
    () => ({
      projectInactive: t("warnHonorariumProjectInactive", {
        ns: NS,
        defaultValue: "Ce projet n'est pas actif ; il ne peut pas être utilisé sur la feuille de temps.",
      }),
      taskNotAvailable: t("warnHonorariumTaskNotAvailable", {
        ns: NS,
        defaultValue: "Cette tâche n'est plus disponible pour ce projet (inactive ou non assignée).",
      }),
      subprojectRequired: t("warnHonorariumSubprojectRequired", {
        ns: NS,
        defaultValue: "Sous-projet requis pour cette ligne.",
      }),
    }),
    [t],
  );

  /** `block: true` — Fermer (et flux soumission) ; `block: false` — met à jour les triangles sans bloquer Enregistrer. */
  const runHonorariumCoherenceCheck = useCallback(
    async (data: U3129Timesheet, options?: { block?: boolean }): Promise<boolean> => {
      const block = options?.block === true;
      const warnings = await u3129ResolveHonorariumCoherenceWarnings(
        data,
        data.employeeId,
        fetchTasksResult,
        honorariumCoherenceMessages,
        admIncludeSubProjectEnabled,
      );
      if (warnings.hasAny) {
        setHonorariumCoherenceWarnings(warnings);
        if (block) {
          setFormActiveTab("tabHonoraria");
          toast.warning(
            t("msgHonorariumCoherenceBlocksClose", {
              ns: NS,
              defaultValue: "Corrigez les lignes signalées dans Honoraires avant de fermer la feuille.",
            }),
          );
          return false;
        }
        return true;
      }
      setHonorariumCoherenceWarnings(null);
      return true;
    },
    [
      admIncludeSubProjectEnabled,
      fetchTasksResult,
      honorariumCoherenceMessages,
      setFormActiveTab,
      t,
      toast,
    ],
  );
  const { initForCreate } = useU3129InitForCreateHandlers();
  const buildUiTimesheet = useCallback(
    (normalized: U3129Timesheet): U3129Timesheet => {
      const employeeDisplay = normalized.employee
        ? [normalized.employee.firstName, normalized.employee.lastName].filter(Boolean).join(" ")
        : "";
      const periodDisplay = formatPeriodDateRange(normalized.periodDate ?? "");
      const statusI18nKey = normalized.status ? `status${normalized.status}` : "";
      const statusDisplay = statusI18nKey
        ? t(statusI18nKey, { ns: NS, defaultValue: normalized.status })
        : (normalized.status ?? "");

      return {
        ...normalized,
        employeeDisplay,
        periodDisplay,
        statusDisplay,
      };
    },
    [t],
  );

  const validateForm = (data: U3129Timesheet): Record<string, string> =>
    u3129CollectTimesheetFormErrors(data, t, { admIncludeSubProjectEnabled, hideExpenseAndEquipmentTabs });

  const u3129TimesheetBL: U2000FormSBusinessHandlers<U3129Timesheet> = {
    newRecord: () => ({ ...emptyTimesheet }),
    validateForm: (data: U3129Timesheet) => validateForm(data),
    saveForme: async (data: U3129Timesheet, editMode: U2000FormStateEnumEditMode) => {
      const correction = parseU3129ApprovalCorrectionLocationState(location.state);
      if (correction) {
        if (!data?.id?.trim() || data.status !== "Open") return;
        if (!(await runHonorariumCoherenceCheck(data, { block: true }))) {
          return;
        }
        setApprovalCorrectionBusy(true);
        try {
          const payload: U3129TimesheetSavePayload = buildTimesheetUpdatePayload(data, (row) => {
            const key = row.clientRowId?.trim();
            const fromRow = key ? honorariumTasksByClientRowId[key] : undefined;
            return fromRow?.find((tk) => tk.id === row.taskType);
          });
          const updateRes = await doUpdate(u2000AxiosAuthenticated, data.id, payload as unknown as Partial<U3129Timesheet>);
          const updatedRaw = updateRes.data?.result ?? updateRes.data;
          const updated = normalizeTimesheetFromApi(updatedRaw as U3129Timesheet);
          const submitted = await submitForApprovalRequest(updated.id!, updated.version);
          const submittedUi = u2000MergeTimesheetDerivedTotalsInto(buildUiTimesheet(normalizeTimesheetFromApi(submitted)));
          formStateHandlers.afterSaveData(submittedUi);
          toast.success(
            t("msgApprovalCorrectionSaved", {
              ns: NS,
              defaultValue: "Corrections enregistrées ; feuille de temps soumise pour approbation.",
            }),
          );
          formStateHandlers.runAfterFormCommitNavigation(() => {
            navigate(correction.returnPath, { replace: true });
          });
        } catch (error: unknown) {
          const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
          if (errorResult.genericErrors?.length) {
            errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
          }
        } finally {
          setApprovalCorrectionBusy(false);
        }
        return;
      }
      await runHonorariumCoherenceCheck(data, { block: false });
      await handleSave.saveData(data, editMode);
    },
    cancelForm: () => {
      const correction = parseU3129ApprovalCorrectionLocationState(location.state);
      if (correction) {
        void (async () => {
          const row = formStateHandlers.getData();
          if (!row?.id?.trim() || row.status !== "Open") return;
          setApprovalCorrectionBusy(true);
          try {
            const submitted = await submitForApprovalRequest(row.id, row.version);
            const submittedUi = u2000MergeTimesheetDerivedTotalsInto(buildUiTimesheet(normalizeTimesheetFromApi(submitted)));
            formStateHandlers.afterSaveData(submittedUi);
            toast.success(
              t("msgApprovalCorrectionCancelled", {
                ns: NS,
                defaultValue: "Correction annulée ; feuille de temps remise en attente d’approbation.",
              }),
            );
            formStateHandlers.runAfterFormCommitNavigation(() => {
              navigate(correction.returnPath, { replace: true });
            });
          } catch (error: unknown) {
            const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
            if (errorResult.genericErrors?.length) {
              errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
            }
          } finally {
            setApprovalCorrectionBusy(false);
          }
        })();
        return;
      }
      if (luStateHandlers.getActionOriginFrom() === "/U3129") {
        navigate("/U3129", { replace: true });
      }
    },
    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onLeaveForm: () => {
      const correction = parseU3129ApprovalCorrectionLocationState(location.state);
      if (correction) {
        navigate(correction.returnPath);
        return;
      }
      navigate("/U3129");
    },
    confirmFormLeaveWithDirty: isApprovalCorrection
      ? () => {
          const correction = parseU3129ApprovalCorrectionLocationState(location.state);
          if (!correction) return;
          void (async () => {
            const row = formStateHandlers.getData();
            if (!row?.id?.trim() || row.status !== "Open") {
              formStateHandlers.runAfterFormCommitNavigation(() => {
                navigate(correction.returnPath, { replace: true });
              });
              return;
            }
            setApprovalCorrectionBusy(true);
            try {
              const submitted = await submitForApprovalRequest(row.id, row.version);
              const submittedUi = u2000MergeTimesheetDerivedTotalsInto(buildUiTimesheet(normalizeTimesheetFromApi(submitted)));
              formStateHandlers.afterSaveData(submittedUi);
              formStateHandlers.runAfterFormCommitNavigation(() => {
                navigate(correction.returnPath, { replace: true });
              });
            } catch (error: unknown) {
              const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
              if (errorResult.genericErrors?.length) {
                errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
              }
            } finally {
              setApprovalCorrectionBusy(false);
            }
          })();
        }
      : undefined,
  };

  const formStateHandlers = useU2000FormStateHandlers<U3129Timesheet>({
    formState,
    formStateDispatch,
    businessHandlers: u3129TimesheetBL,
  });

  const handleSave = useU2000SaveHandlers<U3129Timesheet>({
    doSaveFn: (data, editMode) => {
      const payload: U3129TimesheetSavePayload = buildTimesheetUpdatePayload(data, (row) => {
        const key = row.clientRowId?.trim();
        const fromRow = key ? honorariumTasksByClientRowId[key] : undefined;
        return fromRow?.find((tk) => tk.id === row.taskType);
      });
      const body = payload as unknown as Partial<U3129Timesheet>;
      if (editMode === U2000FormStateEnumEditMode.ADD) return doCreate(u2000AxiosAuthenticated, body);
      return doUpdate(u2000AxiosAuthenticated, data.id, body);
    },
    onSuccess: (saved, editMode) => {
      const prior = formStateHandlers.getData();
      const normalized = saved ? normalizeTimesheetFromApi(saved) : saved;
      const withDisplay = normalized != null ? buildUiTimesheet(normalized) : normalized;
      const toApply =
        withDisplay != null && "undefined" in withDisplay
          ? (() => {
              const rest = Object.fromEntries(
                Object.entries(withDisplay as Record<string, unknown>).filter(([key]) => key !== "undefined"),
              );
              return rest as unknown as U3129Timesheet;
            })()
          : withDisplay;
      if (toApply != null) {
        const honoraria = u3129PreserveHonorariumClientRowIds(prior?.honoraria, toApply.honoraria ?? []);
        formStateHandlers.afterSaveData(
          u2000MergeTimesheetDerivedTotalsInto({ ...toApply, honoraria }),
        );
      }
      toast.success(t("msgLogicalUnitSaved", { ns: NS, defaultValue: "Enregistrement sauvegardé." }));
      const fromList = luStateHandlers.getActionOriginFrom() === "/U3129";
      const isModifyFromList =
        fromList && luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY;
      if (isModifyFromList) {
        navigate("/U3129", { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.ADD && saved?.id) {
        navigate(location.pathname.replace(/create$/, saved.id), { replace: true });
        if (fromList && luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD) {
          luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        }
      }
    },
    onError: ({ error }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      const row = formStateHandlers.getData();
      if (row) {
        void runHonorariumCoherenceCheck(row, { block: false });
      }
    },
  });

  const handleSearch = useU2000SearchHandlers<U3129Timesheet>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      if (!showAllEmployees && (!employeeId || employeeId.trim() === "")) {
        toast.error(t("msgSelectEmployee", { ns: NS }));
        return {
          data: {
            input: {
              filter: searchPayload.filter,
              pagination: searchPayload.pagination,
            },
            results: [],
            queryRowsCount: 0,
          },
        };
      }
      const payload: U3129SearchPayload = {
        ...searchPayload,
        employeeId: employeeId ?? "",
        showAllEmployees,
      };
      return await doSearch(u2000AxiosAuthenticated, payload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3129Timesheet>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());
      const nextRow = searchResult.data[idx];
      if (nextRow?.id) {
        handleGet.getData(nextRow.id);
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U3129Timesheet>({
    doGetFn: (idParam) => doGet(u2000AxiosAuthenticated, idParam),
    onSuccess: (row) => {
      const normalized = normalizeTimesheetFromApi(row);
      const base = buildUiTimesheet(normalized);
      const clean =
        "undefined" in base
          ? (() => {
              const rest = Object.fromEntries(
                Object.entries(base as Record<string, unknown>).filter(([key]) => key !== "undefined"),
              );
              return rest as unknown as U3129Timesheet;
            })()
          : base;
      const withTotals = u2000MergeTimesheetDerivedTotalsInto(clean);
      if (normalized.id) {
        gridStateHandlers.setDataById(normalized.id, withTotals);
      }
      formStateHandlers.setData(withTotals);
      const newPath = location.pathname.replace(/(?:create|[\w-]+)$/, normalized.id);
      if (newPath !== location.pathname) {
        navigate(newPath, { replace: true, state: location.state });
      }
      const correctionNav = parseU3129ApprovalCorrectionLocationState(location.state);
      const { employeeLabel, detailLabel } = buildU3129TimesheetDetailBreadcrumbTrail(clean, normalized, i18n.language);
      if (correctionNav) {
        setLogicalUnitBreadcrumb([
          { href: correctionNav.returnPath, label: t("breadcrumbApprovalCorrectionReturn", { ns: NS, defaultValue: "Approbation feuille de temps" }) },
          { label: t("breadcrumbApprovalCorrection", { ns: NS, defaultValue: "Correction approbation" }) },
          ...(employeeLabel ? [{ label: employeeLabel }] : []),
          { label: detailLabel },
        ]);
        formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      } else {
        setLogicalUnitBreadcrumb([
          { href: "/U3129", label: t("title", { ns: NS }) },
          ...(employeeLabel ? [{ label: employeeLabel }] : []),
          { label: detailLabel },
        ]);
        if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
          formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
        }
      }
    },
    onError: ({ error }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
    },
  });

  const handleCloseTimesheetForApproval = useCallback(async () => {
    const row = formStateHandlers.getData();
    if (!row?.id?.trim() || row.status !== "Open") return;
    setCloseTimesheetLoading(true);
    try {
      if (!(await runHonorariumCoherenceCheck(row, { block: true }))) {
        return;
      }

      let toSubmit = row;
      if (formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT) {
        const payload: U3129TimesheetSavePayload = buildTimesheetUpdatePayload(row, (line) => {
          const key = line.clientRowId?.trim();
          const fromRow = key ? honorariumTasksByClientRowId[key] : undefined;
          return fromRow?.find((tk) => tk.id === line.taskType);
        });
        const updateRes = await doUpdate(u2000AxiosAuthenticated, row.id, payload as unknown as Partial<U3129Timesheet>);
        const updatedRaw = updateRes.data?.result ?? updateRes.data;
        toSubmit = normalizeTimesheetFromApi(updatedRaw as U3129Timesheet);
      }

      const saved = await submitForApprovalRequest(toSubmit.id!, toSubmit.version);
      const normalized = normalizeTimesheetFromApi(saved);
      const clean = u2000MergeTimesheetDerivedTotalsInto(buildUiTimesheet(normalized));
      if (normalized.id) {
        gridStateHandlers.setDataById(normalized.id, clean);
      }
      formStateHandlers.afterSaveData(clean);
      setHonorariumCoherenceWarnings(null);
      toast.success(t("msgSubmitForApprovalSuccess", { ns: NS, defaultValue: "Feuille de temps soumise pour approbation." }));
      const { employeeLabel, detailLabel } = buildU3129TimesheetDetailBreadcrumbTrail(clean, normalized, i18n.language);
      setLogicalUnitBreadcrumb([
        { href: "/U3129", label: t("title", { ns: NS }) },
        ...(employeeLabel ? [{ label: employeeLabel }] : []),
        { label: detailLabel },
      ]);
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      void runHonorariumCoherenceCheck(row, { block: false });
    } finally {
      setCloseTimesheetLoading(false);
    }
  }, [
    buildUiTimesheet,
    formStateHandlers,
    gridStateHandlers,
    honorariumTasksByClientRowId,
    i18n.language,
    runHonorariumCoherenceCheck,
    setLogicalUnitBreadcrumb,
    submitForApprovalRequest,
    t,
    toast,
    u2000AxiosAuthenticated,
  ]);

  const handleReopenTimesheet = useCallback(async () => {
    const row = formStateHandlers.getData();
    if (!row?.id?.trim() || row.status !== "WaitingForApproval") return;
    setReopenTimesheetLoading(true);
    try {
      const saved = await reopenRequest(row.id, row.version);
      const normalized = normalizeTimesheetFromApi(saved);
      const clean = u2000MergeTimesheetDerivedTotalsInto(buildUiTimesheet(normalized));
      if (normalized.id) {
        gridStateHandlers.setDataById(normalized.id, clean);
      }
      formStateHandlers.afterSaveData(clean);
      toast.success(t("msgReopenTimesheetSuccess", { ns: NS, defaultValue: "Feuille de temps réouverte." }));
      const { employeeLabel, detailLabel } = buildU3129TimesheetDetailBreadcrumbTrail(clean, normalized, i18n.language);
      setLogicalUnitBreadcrumb([
        { href: "/U3129", label: t("title", { ns: NS }) },
        ...(employeeLabel ? [{ label: employeeLabel }] : []),
        { label: detailLabel },
      ]);
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    } finally {
      setReopenTimesheetLoading(false);
    }
  }, [buildUiTimesheet, formStateHandlers, gridStateHandlers, i18n.language, reopenRequest, setLogicalUnitBreadcrumb, t, toast]);

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [formStateHandlers.getData()],
    doDeleteFn: async (idParam, row) => doDelete(u2000AxiosAuthenticated, idParam, row as unknown as Record<string, unknown>),
    onSuccess: () => {
      toast.success(t("msgLogicalUnitDeleted", { ns: NS, defaultValue: "Enregistrement supprimé." }));
      navigate("/U3129", { replace: true });
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      });
    },
  });

  const periodDateFromState = (location.state as { periodDate?: string } | null)?.periodDate;
  const employeeFromState = (location.state as { employee?: U3129FindEmployeeItem | null } | null)?.employee ?? null;

  // Création : formulaire vide puis enrichissement optionnel via GET initForCreate (employé + période requis).
  useEffect(() => {
    const isCreate = id === undefined || id === "create" || location.pathname.endsWith("/create");
    if (!isCreate) return;
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    const periodDate = periodDateFromState ?? "";
    const employeeIdResolved = employeeFromState?.id ?? employee?.id ?? "";
    const employeeResolved = employeeFromState ?? (employee ? { ...employee } : undefined);
    const basePayload: U3129Timesheet = {
      ...emptyTimesheet,
      bankHoursList: ensureBankHoursList(emptyTimesheet.bankHoursList),
      employeeId: employeeIdResolved,
      employee: employeeResolved,
      employeeDisplay:
        employeeFromState != null
          ? [employeeFromState.firstName, employeeFromState.lastName].filter(Boolean).join(" ")
          : employee
            ? [employee.firstName, employee.lastName].filter(Boolean).join(" ")
            : "",
      periodDate,
      periodDisplay: formatPeriodDateRange(periodDate),
    };
    formStateHandlers.setData(basePayload);
    setLogicalUnitBreadcrumb([
      { href: "/U3129", label: t("title", { ns: NS }) },
      { label: t("breadcrumbNewTimesheet", { ns: NS, defaultValue: "Nouvelle feuille de temps" }) },
    ]);

    const canFetchInit = employeeIdResolved.trim() !== "" && periodDate.trim() !== "";
    if (!canFetchInit) {
      setCreateInitLoading(false);
      return;
    }

    let cancelled = false;
    setCreateInitLoading(true);
    void initForCreate({ employeeId: employeeIdResolved, periodDate })
      .then((normalized) => {
        if (cancelled || normalized == null) return;
        const merged: U3129Timesheet = {
          ...normalized,
          id: normalized.id?.trim() ?? "",
          employeeId: normalized.employeeId?.trim() || employeeIdResolved,
          employee:
            normalized.employee != null
              ? { ...((employeeResolved ?? {}) as U3129EmployeeRef), ...normalized.employee }
              : employeeResolved,
          periodDate: normalized.periodDate?.trim() || periodDate,
        };
        formStateHandlers.setData(u2000MergeTimesheetDerivedTotalsInto(buildUiTimesheet(merged)));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        } else {
          toast.error(t("msgCreateInitFallback", { ns: NS, defaultValue: "Impossible de charger les valeurs par défaut. Vous pouvez saisir la feuille manuellement." }));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCreateInitLoading(false);
        }
      });

    return () => {
      cancelled = true;
      setCreateInitLoading(false);
    };
    // Dépendre de `employee?.id` (pas `employee`) pour éviter de relancer l’effet au moindre changement de référence d’objet,
    // ce qui appelait `setData(basePayload)` et écrasait le résultat de `initForCreate` (soldes banque sur `employee`).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- buildUiTimesheet / formStateHandlers stables pour ce flux création
  }, [id, location.pathname, employee?.id, periodDateFromState, employeeFromState?.id, initForCreate]);

  // Édition : charger la feuille une seule fois. Pas de dépendance sur employee pour éviter un re-GET inutile.
  useEffect(() => {
    const isCreate = id === undefined || id === "create" || location.pathname.endsWith("/create");
    if (isCreate || !id) return;
    handleGet.getData(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.pathname]);

  const data = formStateHandlers.getData();

  useEffect(() => {
    if (data == null) return;

    const derived = u2000ComputeTimesheetDerivedTotals(data);

    const needsUpdate =
      toFiniteNumber(data.totalPaidHours) !== derived.totalPaidHours ||
      toFiniteNumber(data.totalLeaveHours) !== derived.totalLeaveHours ||
      toFiniteNumber(data.totalSpecialWithdraw) !== derived.totalSpecialWithdraw ||
      toFiniteNumber(data.totalSpecialDeposit) !== derived.totalSpecialDeposit ||
      toFiniteNumber(data.totalSpecialNet) !== derived.totalSpecialNet ||
      toFiniteNumber(data.totalExpenses) !== derived.totalExpenses ||
      toFiniteNumber(data.totalEquipment) !== derived.totalEquipment ||
      toFiniteNumber(data.approvalPaidHours) !== derived.approvalPaidHours;

    if (!needsUpdate) return;

    formStateHandlers.setData({
      ...data,
      ...derived,
    });
  }, [data, formStateHandlers]);

  const honorariaCount = data?.honoraria?.length ?? 0;
  const absencesBanquesCount = useMemo(() => {
    const list = ensureBankHoursList(data?.bankHoursList);
    return list.filter((row) => U2000_TIMESHEET_WEEK_DAYS.some((d) => (Number(row[d]) || 0) > 0)).length;
  }, [data?.bankHoursList]);
  const expensesCount = data?.expenses?.length ?? 0;
  const equipmentCount = data?.equipment?.length ?? 0;
  const allFormTabs = useMemo(
    () => [
    {
      id: "tabHonoraria",
      collapsedRailLabel: t("tabHonorariaRailAbbrev", { ns: NS, defaultValue: "Hon." }),
      collapsedRailTitle: t("tabHonoraria", { ns: NS, defaultValue: "Honoraires" }),
      label: (
        <span className="inline-flex items-center gap-1.5">
          {t("tabHonoraria", { ns: NS, defaultValue: "Honoraires" })}
          {honorariaCount > 0 && <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">{honorariaCount}</span>}
        </span>
      ),
      content: (
        <U3129HonorariumTab formStateHandlers={formStateHandlers} coherenceWarnings={honorariumCoherenceWarnings} />
      ),
    },
    {
      id: "tabAbsencesBanques",
      collapsedRailLabel: t("tabAbsencesBanquesRailAbbrev", { ns: NS, defaultValue: "Abs." }),
      collapsedRailTitle: t("tabAbsencesBanques", { ns: NS, defaultValue: "Absences et banques" }),
      label: (
        <span className="inline-flex items-center gap-1.5">
          {t("tabAbsencesBanques", { ns: NS, defaultValue: "Absences et banques" })}
          {absencesBanquesCount > 0 && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">{absencesBanquesCount}</span>}
        </span>
      ),
      content: <U3129AbsencesBanquesTab formStateHandlers={formStateHandlers} />,
    },
    {
      id: "tabExpenses",
      collapsedRailLabel: t("tabExpensesRailAbbrev", { ns: NS, defaultValue: "Dép." }),
      collapsedRailTitle: t("tabExpenses", { ns: NS, defaultValue: "Dépenses" }),
      label: (
        <span className="inline-flex items-center gap-1.5">
          {t("tabExpenses", { ns: NS, defaultValue: "Dépenses" })}
          {expensesCount > 0 && <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">{expensesCount}</span>}
        </span>
      ),
      content: <U3129ExpenseTab formStateHandlers={formStateHandlers} />,
    },
    {
      id: "tabEquipment",
      collapsedRailLabel: t("tabEquipmentRailAbbrev", { ns: NS, defaultValue: "Éq." }),
      collapsedRailTitle: t("tabEquipment", { ns: NS, defaultValue: "Équipement" }),
      label: (
        <span className="inline-flex items-center gap-1.5">
          {t("tabEquipment", { ns: NS, defaultValue: "Équipement" })}
          {equipmentCount > 0 && <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">{equipmentCount}</span>}
        </span>
      ),
      content: <U3129EquipmentTab formStateHandlers={formStateHandlers} />,
    },
  ],
    [
      t,
      honorariaCount,
      absencesBanquesCount,
      expensesCount,
      equipmentCount,
      formStateHandlers,
      honorariumCoherenceWarnings,
    ],
  );

  const visibleFormTabs = useMemo(() => {
    if (!hideExpenseAndEquipmentTabs) {
      return allFormTabs;
    }
    const hidden = new Set<string>(U3129_HIDDEN_EXPENSE_EQUIPMENT_TAB_IDS);
    return allFormTabs.filter((tab) => !hidden.has(tab.id));
  }, [allFormTabs, hideExpenseAndEquipmentTabs]);

  useEffect(() => {
    if (!hideExpenseAndEquipmentTabs) {
      return;
    }
    if ((U3129_HIDDEN_EXPENSE_EQUIPMENT_TAB_IDS as readonly string[]).includes(formActiveTab)) {
      setFormActiveTab("tabHonoraria");
    }
  }, [hideExpenseAndEquipmentTabs, formActiveTab, setFormActiveTab]);

  const tabsWithErrors = useMemo(() => {
    const e = formState.errors as Record<string, string>;
    const tabs: string[] = [];
    if (honorariaTabHasValidationErrors(e) || honorariumCoherenceWarnings?.hasAny) tabs.push("tabHonoraria");
    if (bankHoursTabHasValidationErrors(e)) tabs.push("tabAbsencesBanques");
    if (!hideExpenseAndEquipmentTabs && expensesTabHasValidationErrors(e)) tabs.push("tabExpenses");
    if (!hideExpenseAndEquipmentTabs && equipmentTabHasValidationErrors(e)) tabs.push("tabEquipment");
    return tabs;
  }, [formState.errors, hideExpenseAndEquipmentTabs, honorariumCoherenceWarnings?.hasAny]);

  if (data === null && id && id !== "create") {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-gray-600">{t("msgLoading", { ns: NS, defaultValue: "Chargement..." })}</p>
      </div>
    );
  }

  return (
    <>
      <U3129AddTimesheetDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onConfirm={(periodDate, selectedEmployee) => {
          setAddDialogOpen(false);
          if (selectedEmployee?.id) {
            setEmployeeId(selectedEmployee.id);
          }
          navigate("/U3129/create", { state: { periodDate, employee: selectedEmployee ?? null } });
        }}
        employee={employee}
      />
    <U3129TimesheetDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
    <U3129TimesheetWeekViewProvider periodDate={data?.periodDate}>
    <U2000DefaultForm
      fillMainContentHeight
      hideHeaderSaveCancelInEditMode
      hideHeaderToolbarInEditMode
      actionsBtns={
        <U3129TimesheetFormToolBar
          formStateHandlers={formStateHandlers}
          approvalCorrectionMode={isApprovalCorrection}
          onAdd={() => setAddDialogOpen(true)}
          onModify={() => {
            const row = formStateHandlers.getData();
            if (row && row.status !== "Open") {
              toast.error(t("msgTimesheetMutateRequiresOpen", { ns: NS, defaultValue: "Cette action n’est autorisée que pour une feuille de temps au statut Ouvert." }));
              return;
            }
            formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
          }}
          onDelete={() => {
            const row = formStateHandlers.getData();
            if (!row || row.status !== "Open") {
              toast.error(t("msgTimesheetMutateRequiresOpen", { ns: NS, defaultValue: "Cette action n’est autorisée que pour une feuille de temps au statut Ouvert." }));
              return;
            }
            handleDelete.handleDelete();
          }}
          onCloseTimesheet={handleCloseTimesheetForApproval}
          closeTimesheetLoading={closeTimesheetLoading}
          onReopenTimesheet={handleReopenTimesheet}
          reopenTimesheetLoading={reopenTimesheetLoading}
        />
      }
      searchStateHandlers={searchStateHandlers}
      gridStateHandlers={gridStateHandlers}
      formStateHandlers={formStateHandlers}
    >
      <div className="mt-1 flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-hidden">
        {isApprovalCorrection && (
          <p
            className="shrink-0 rounded-lg border border-blue-200 bg-blue-50/80 px-4 py-2 text-sm text-blue-900"
            role="status"
            aria-busy={approvalCorrectionBusy}
          >
            {t("msgApprovalCorrectionBanner", {
              ns: NS,
              defaultValue: "Correction pour approbation : enregistrer pour soumettre à nouveau, ou annuler pour revenir sans modifier les données.",
            })}
          </p>
        )}
        {createInitLoading && (
          <p className="shrink-0 rounded-lg border border-blue-200 bg-blue-50/80 px-4 py-2 text-sm text-blue-900" role="status">
            {t("msgCreateInitLoading", { ns: NS, defaultValue: "Chargement des valeurs par défaut…" })}
          </p>
        )}
        <div className="shrink-0">
          <U3129TimesheetFormHeaderTab formStateHandlers={formStateHandlers} />
        </div>
        {visibleFormTabs.length > 0 && (
          <U2000Tab
            tabs={visibleFormTabs}
            defaultTab="tabHonoraria"
            activeTab={formActiveTab}
            setActiveTab={(tab) => setFormActiveTab(typeof tab === "string" ? tab : String(tab))}
            tabsWithErrors={tabsWithErrors}
            tabValidationErrorTooltip={t("lblTabHasValidationErrors", {
              ns: NS,
              defaultValue: "Il y a des erreurs",
            })}
            fillRemainingHeight
            tabListOrientation={timesheetFormTabListOrientation}
            activeIndicator={timesheetFormTabListOrientation === "vertical" ? "border" : "underline"}
            tabPanelClassName={timesheetFormTabListOrientation === "vertical" ? "p-4" : undefined}
            className="min-h-0 min-w-0 flex-1"
            tabListCollapsible={timesheetFormTabListOrientation === "vertical"}
            tabListCollapsed={formTabListCollapsed}
            onTabListCollapsedChange={setFormTabListCollapsed}
            expandTabListLabel={t("btnExpandFormTabs", { ns: NS, defaultValue: "Développer les onglets" })}
            collapseTabListLabel={t("btnCollapseFormTabs", { ns: NS, defaultValue: "Replier les onglets" })}
          />
        )}
      </div>
    </U2000DefaultForm>
    </U3129TimesheetWeekViewProvider>
    </div>
    </>
  );
};

export default U3129TimesheetForm;
